import { removeDiacritics } from './index.js';
export function computePatientSearchText(payload) {
    const parts = [
        removeDiacritics(payload.givenName).toLowerCase(),
        removeDiacritics(payload.familyName).toLowerCase(),
        payload.hospitalPatientId ?? '',
        payload.nationalId ?? '',
        payload.insuranceNumber ?? '',
    ];
    return parts.filter(Boolean).join(' ');
}
/**
 * The words of a patient search, ready to match one by one: diacritics removed, lower case, and
 * `%`, `_` and `\` escaped so a typed character stays a character instead of a LIKE wildcard.
 * Names are shown "Surname, First name", so every word has to match regardless of the order.
 */
export function getPatientSearchWords(search) {
    if (!search)
        return [];
    return removeDiacritics(search)
        .toLowerCase()
        .split(/\s+/)
        .map(word => word.trim())
        .filter(Boolean)
        .map(word => word.replace(/[\\%_]/g, '\\$&'));
}
/**
 * Canonical form used to compare patient identifiers (national ID, hospital ID, insurance number):
 * whitespace and letter case are ignored, so "92 0101 12345" and "92010112345" are the same number.
 */
export function normalizePatientIdentifier(value) {
    return (value ?? '').replace(/\s+/g, '').toLowerCase();
}
/**
 * Patient step of Scan mode administration (CY-326): whether a scanned wristband hospital ID identifies
 * `patient`. Only an exact, case-sensitive match (outer whitespace ignored) passes, and only if no other
 * candidate shares the ID under `normalizePatientIdentifier`, since such a wristband can't tell the two apart.
 * On a mismatch, `scannedPatientId` is the other patient the ID belongs to (exact match first), if any.
 */
export function matchWristbandScan(scanned, patient, candidates) {
    const hospitalId = scanned.trim();
    if (!hospitalId)
        return { matches: false, scannedPatientId: null };
    const normalized = normalizePatientIdentifier(hospitalId);
    const others = candidates.filter(candidate => candidate.id !== patient.id && normalizePatientIdentifier(candidate.hospitalPatientId) === normalized);
    const exactOther = others.find(candidate => candidate.hospitalPatientId?.trim() === hospitalId);
    if (patient.hospitalPatientId?.trim() === hospitalId && !others.length)
        return { matches: true };
    return { matches: false, scannedPatientId: (exactOther ?? others[0])?.id ?? null };
}
/** Completed months of age at `at`; birth dates are date-only (UTC midnight), so UTC fields are compared. */
export function getAgeInMonths(birthDate, at) {
    const birth = new Date(birthDate);
    const date = new Date(at);
    let months = (date.getUTCFullYear() - birth.getUTCFullYear()) * 12 + (date.getUTCMonth() - birth.getUTCMonth());
    if (date.getUTCDate() < birth.getUTCDate())
        months--;
    return Math.max(months, 0);
}
/**
 * ZQL has no REPLACE()/LOWER() for column values, so to find identifiers that differ only by whitespace we
 * match `%` between every character with ILIKE. This over-matches by design; callers must confirm a match by
 * comparing `normalizePatientIdentifier` of both sides. Backslashes become `_` so they can't act as escapes.
 */
export function toPatientIdentifierPattern(value) {
    const chars = [...normalizePatientIdentifier(value).replaceAll('\\', '_')];
    return `%${chars.join('%')}%`;
}
export const PATIENT_IDENTIFIER_FIELDS = ['nationalId', 'hospitalPatientId', 'insuranceNumber'];
function candidateIdentifier(candidate, field) {
    return field === 'insuranceNumber' ? candidate.insurance?.insuranceNumber : candidate[field];
}
/**
 * Returns, per identifier field, the first candidate patient that already uses the same number.
 * Empty values are never considered duplicates.
 */
export function findDuplicatePatientIdentifiers(candidates, values) {
    const duplicates = {};
    for (const field of PATIENT_IDENTIFIER_FIELDS) {
        const value = normalizePatientIdentifier(values[field]);
        if (!value)
            continue;
        const match = candidates.find(candidate => normalizePatientIdentifier(candidateIdentifier(candidate, field)) === value);
        if (match)
            duplicates[field] = match;
    }
    return duplicates;
}
//# sourceMappingURL=patients.js.map